import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import api from 'services/api';
import { Permissao, StatusLocucao, StatusRoteiroPromo } from 'utils/variables';
import { Chamada, Patrocinio, RoteiroPromo } from 'pages/@types';
import { show } from 'store/modules/dialog/actions';
import AppError from 'utils/AppError';
import { usePermit } from 'utils/hooks/usePermit';

import Button from 'components/Button';
import Spinner from 'components/Spinner';
import Card from 'components/Card';
import Loader from 'components/ContentLoader/GenericLoader';
import NaoAutorizado from 'assets/naoautorizado.svg';
import LocucaoGravada from 'assets/locucaogravada.svg';

import { Container, Content, Scroll } from './styles';
import { FiMic } from 'react-icons/fi';
import NotFound from 'components/NotFound';

interface Dados {
  locucao: string;
  tipo: string;
  retranca: string;
  duracao: string;
  patrocinios?: Patrocinio[];
  setor?: string;
  locucaobase?: string;
  duracaobaseprograma?: string;
  observacaolocucao?: string;
}

export default function LocucaoView() {
  const [loading, setLoading] = useState(false);
  const [locucaoGravada, setLocucaoGravada] = useState(false);

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { roteiroId } = useParams<{
    roteiroId: string;
  }>();
  const { chamadaId } = useParams<{
    chamadaId: string;
  }>();
  const [dados, setDados] = useState<Dados>({
    locucao: '',
    tipo: '',
    setor: '',
    locucaobase: '',
    duracaobaseprograma: '',
    retranca: '',
    duracao: '',
    patrocinios: [],
    observacaolocucao: '',
  });

  const hasLocucao = usePermit([], [Permissao.LOCUCAO]); // comunicador
  const hasAvaliador = usePermit([], [Permissao.AVALIADOR]);

  const dispatch = useDispatch();

  const handleMessage = useCallback(
    ({ contentHTML, redirectTo, type }) => {
      const option = {
        type: type,
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(async () => {
    setLoadingSubmit(true);
    try {
      if (chamadaId) {
        await api.patch<Chamada>(`/chamadasts/${chamadaId}/statuslocucao`, {
          statuslocucao: StatusLocucao.GRAVADO,
        });
      } else {
        await api.patch<RoteiroPromo>(`/roteiropromo/${roteiroId}`, {
          status: StatusRoteiroPromo.FINALIZADO,
        });
      }
      handleMessage({
        contentHTML: `<h3>Locução Gravada!</h3> <p>Você será redirecionado em 3 segundos</p>`,
        redirectTo: chamadaId ? '/chamadas' : '/roteiropromo',
        type: 'success',
      });
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        const appError = err as AppError;
        console.log(appError);
        toast.error('Atualização de status inválida');
      }
    }
    setLoadingSubmit(false);
  }, [chamadaId]);

  async function loadRoteiroPromo(roteiroId: string) {
    setLoading(true);
    try {
      const response = await api.get(`/roteiropromo/${roteiroId}`);
      if (response && response.data) {
        if (
          response.data.status !== StatusRoteiroPromo.LOCUCAO ||
          !response.data.locucao
        ) {
          setLocucaoGravada(true);
        }
        setDados(response.data);
      } else toast.error('Falha de comunicação com o servidor');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        const appError = error as AppError;
        toast.error(appError.message || 'Erro inesperado.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadChamada(chamadaId: string) {
    setLoading(true);
    try {
      const response = await api.get(`/chamadasts/${chamadaId}`);
      if (response && response.data) {
        if (response.data.chamada.statuslocucao !== StatusLocucao.SOLICITADO)
          setLocucaoGravada(true);
        else {
          const sortedPatrocinios = response.data.chamada.patrocinios?.sort(
            (a, b) => (a.ordem || 0) - (b.ordem || 0),
          );
          setDados({
            ...response.data.chamada,
            duracaobaseprograma: response.data.chamada.baseprograma?.duracao,
            patrocinios: sortedPatrocinios || [],
          });
        }
      } else toast.error('Falha de comunicação com o servidor');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        const appError = error as AppError;
        toast.error(appError.message || 'Erro inesperado.');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (roteiroId) {
      loadRoteiroPromo(roteiroId);
    } else if (chamadaId) {
      loadChamada(chamadaId);
    }
  }, []);

  return (
    <Container>
      <Card>
        {loading ? (
          <Loader />
        ) : !hasLocucao && !hasAvaliador ? (
          <div>
            <NotFound img={NaoAutorizado} title="ACESSO NÃO AUTORIZADO">
              Você não tem permissão para acessar essa página
            </NotFound>
          </div>
        ) : locucaoGravada ? (
          <NotFound img={LocucaoGravada} title="LOCUÇÃO JÁ GRAVADA">
            Locução dessa mídia já gravada
          </NotFound>
        ) : (
          <Content>
            <Scroll>
              <header>
                <h2>
                  {dados.retranca} ({dados.duracao}s)
                </h2>
              </header>
              <div>
                {dados.locucaobase && (
                  <div>
                    <h3>
                      {dados.locucaobase} (Duração:{' '}
                      {dados.duracaobaseprograma || 0}s)
                    </h3>
                    {dados.patrocinios?.map(
                      patrocinio =>
                        patrocinio.locucao && (
                          <h3>{`${patrocinio.locucao} (Duração: ${patrocinio
                            .comercial?.duracao || 0}s)`}</h3>
                        ),
                    )}
                  </div>
                )}
                {dados.locucao && <h3>{dados.locucao}</h3>}
              </div>
              <div>
                {dados.observacaolocucao && (
                  <h4>* {dados.observacaolocucao}</h4>
                )}
              </div>
            </Scroll>
            <div>
              <Button
                onClick={handleSubmit}
                type="submit"
                color="primary"
                disabled={loadingSubmit}
              >
                <FiMic />
                {!loadingSubmit ? (
                  'SINALIZAR GRAVAÇÃO'
                ) : (
                  <Spinner type="ThreeDots" />
                )}
              </Button>
            </div>
          </Content>
        )}
      </Card>
    </Container>
  );
}
